const DEFAULT_ELO = 1000;
const K_FACTOR = 32;

export interface EloPlayer {
  id: number;
  rating: number;
}

function expectedScore(ratingA: number, ratingB: number): number {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

/** Compute Elo ratings from chronological match history. */
export function computeEloRatings(
  playerIds: number[],
  matches: {
    teama: number[];
    teamb: number[];
    winner: string | null;
    match_date: string;
  }[]
): Map<number, number> {
  const ratings = new Map<number, number>();
  for (const id of playerIds) ratings.set(id, DEFAULT_ELO);

  const sorted = [...matches].sort(
    (a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
  );

  for (const match of sorted) {
    const teamA = match.teama ?? [];
    const teamB = match.teamb ?? [];
    if (!teamA.length || !teamB.length || !match.winner || match.winner === "Draw") continue;

    const avgA =
      teamA.reduce((sum, id) => sum + (ratings.get(id) ?? DEFAULT_ELO), 0) / teamA.length;
    const avgB =
      teamB.reduce((sum, id) => sum + (ratings.get(id) ?? DEFAULT_ELO), 0) / teamB.length;

    const scoreA = match.winner === "A" ? 1 : 0;
    const scoreB = 1 - scoreA;
    const expA = expectedScore(avgA, avgB);
    const expB = expectedScore(avgB, avgA);

    for (const id of teamA) {
      const current = ratings.get(id) ?? DEFAULT_ELO;
      ratings.set(id, Math.round(current + K_FACTOR * (scoreA - expA)));
    }
    for (const id of teamB) {
      const current = ratings.get(id) ?? DEFAULT_ELO;
      ratings.set(id, Math.round(current + K_FACTOR * (scoreB - expB)));
    }
  }

  return ratings;
}

export function calculateBestWinStreak(
  matches: { teama?: number[]; teamb?: number[]; winner: string | null; match_date: string }[],
  playerId: number
): number {
  const playerMatches = matches
    .filter((m) => m.teama?.includes(playerId) || m.teamb?.includes(playerId))
    .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());

  let best = 0;
  let current = 0;
  for (const match of playerMatches) {
    const inA = match.teama?.includes(playerId);
    const won = (inA && match.winner === "A") || (!inA && match.winner === "B");
    if (won) {
      current++;
      best = Math.max(best, current);
    } else {
      current = 0;
    }
  }
  return best;
}

export function calculateCurrentStreakWithSign(
  matches: { teama?: number[]; teamb?: number[]; winner: string | null; match_date: string }[],
  playerId: number
): { streak: number; isWinStreak: boolean } {
  const playerMatches = matches
    .filter((m) => m.teama?.includes(playerId) || m.teamb?.includes(playerId))
    .sort((a, b) => new Date(b.match_date).getTime() - new Date(a.match_date).getTime());

  if (!playerMatches.length) return { streak: 0, isWinStreak: true };

  const first = playerMatches[0];
  const inA = first.teama?.includes(playerId);
  const firstWon = (inA && first.winner === "A") || (!inA && first.winner === "B");
  const firstLost =
    first.winner !== "Draw" &&
    !((inA && first.winner === "A") || (!inA && first.winner === "B"));

  let streak = 0;
  for (const match of playerMatches) {
    const isInA = match.teama?.includes(playerId);
    const won = (isInA && match.winner === "A") || (!isInA && match.winner === "B");
    const lost =
      match.winner !== "Draw" && !won;

    if (firstWon && won) streak++;
    else if (firstLost && lost) streak++;
    else break;
  }

  return { streak, isWinStreak: firstWon };
}
